// ============================================================================
// useNetworkStatus Hook - Reactive Online/Offline Detection
// EduPath Africa v2.0 - Sprint 3: Offline Assessment Infrastructure
// ============================================================================
//
// FILE: src/hooks/useNetworkStatus.ts
//
// Provides:
//   - isOnline: boolean - current network state
//   - wasOffline: boolean - true if user was recently offline (for sync prompts)
//   - connectionQuality: 'good' | 'slow' | 'offline' - estimated connection quality
//   - lastOnlineAt: Date | null - when user was last online
//
// Uses navigator.onLine + periodic fetch pings for reliable detection.
// navigator.onLine alone is unreliable (returns true on captive portals).
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';

export type ConnectionQuality = 'good' | 'slow' | 'offline';

export interface NetworkStatus {
  /** Whether the browser currently has network connectivity */
  isOnline: boolean;
  /** Whether the user was offline earlier in this session (triggers sync) */
  wasOffline: boolean;
  /** Estimated connection quality based on ping latency */
  connectionQuality: ConnectionQuality;
  /** Timestamp of last confirmed online state */
  lastOnlineAt: Date | null;
  /** Number of items waiting to sync */
  pendingSyncCount: number;
}

// Ping endpoint - use a tiny resource that won't be blocked by CORS
// Falls back to navigator.onLine if ping fails
const PING_URL = 'https://65563g1ia9.execute-api.eu-west-1.amazonaws.com/dev/health';
const PING_INTERVAL_ONLINE = 60_000;   // Check every 60s when online
const PING_INTERVAL_OFFLINE = 10_000;  // Check every 10s when offline
const PING_TIMEOUT = 5_000;            // 5s timeout
const SLOW_THRESHOLD_MS = 3_000;       // >3s = slow connection

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(
    navigator.onLine ? 'good' : 'offline'
  );
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(
    navigator.onLine ? new Date() : null
  );
  const [pendingSyncCount, setPendingSyncCount] = useState<number>(0);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasOfflineRef = useRef(false);

  // Perform an actual network check via fetch
  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT);
      const start = Date.now();

      const response = await fetch(PING_URL, {
        method: 'HEAD',
        mode: 'no-cors',     // Won't fail on CORS, just returns opaque response
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const latency = Date.now() - start;

      // Any response (even opaque) means we're online
      if (response.type === 'opaque' || response.ok) {
        setConnectionQuality(latency > SLOW_THRESHOLD_MS ? 'slow' : 'good');
        return true;
      }
      return false;
    } catch {
      // Network error or timeout = offline
      return false;
    }
  }, []);

  // Update pending sync count from IndexedDB
  const updateSyncCount = useCallback(async () => {
    try {
      const { syncQueue } = await import('@/services/offlineStore');
      const count = await syncQueue.count();
      setPendingSyncCount(count);
    } catch {
      // offlineStore not available yet
    }
  }, []);

  // Handle connectivity change
  const handleConnectivityChange = useCallback(
    (online: boolean) => {
      setIsOnline(online);

      if (online) {
        setLastOnlineAt(new Date());
        setConnectionQuality('good');

        // If we were offline, mark wasOffline for sync trigger
        if (wasOfflineRef.current) {
          setWasOffline(true);
          // Dispatch custom event for sync service to pick up
          window.dispatchEvent(new CustomEvent('edupath:back-online'));
        }
      } else {
        setConnectionQuality('offline');
        wasOfflineRef.current = true;
        setWasOffline(true);
        // Dispatch custom event for UI components
        window.dispatchEvent(new CustomEvent('edupath:went-offline'));
      }
    },
    []
  );

  // Set up event listeners and polling
  useEffect(() => {
    const handleOnline = () => handleConnectivityChange(true);
    const handleOffline = () => handleConnectivityChange(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connectivity check
    checkConnectivity().then((online) => {
      if (!online && navigator.onLine) {
        // navigator.onLine is wrong (captive portal, etc.)
        handleConnectivityChange(false);
      }
    });

    // Periodic connectivity check
    const startPolling = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      const interval = isOnline ? PING_INTERVAL_ONLINE : PING_INTERVAL_OFFLINE;
      pingIntervalRef.current = setInterval(async () => {
        const online = await checkConnectivity();
        if (online !== isOnline) {
          handleConnectivityChange(online);
        }
        // Also update sync count periodically
        updateSyncCount();
      }, interval);
    };

    startPolling();
    updateSyncCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [isOnline, checkConnectivity, handleConnectivityChange, updateSyncCount]);

  // Listen for sync queue changes
  useEffect(() => {
    const handleSyncChange = () => updateSyncCount();
    window.addEventListener('edupath:sync-queue-changed', handleSyncChange);
    return () => window.removeEventListener('edupath:sync-queue-changed', handleSyncChange);
  }, [updateSyncCount]);

  return {
    isOnline,
    wasOffline,
    connectionQuality,
    lastOnlineAt,
    pendingSyncCount,
  };
}

// ============================================================================
// Singleton network status (for use outside React components)
// ============================================================================

let _isOnline = navigator.onLine;

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { _isOnline = true; });
  window.addEventListener('offline', () => { _isOnline = false; });
}

/**
 * Check if currently online (non-reactive, for use in services)
 */
export function isOnline(): boolean {
  return _isOnline;
}

export default useNetworkStatus;
